import { useCallback, useMemo } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from './useLanguage';
import { getNavSectionsForScope } from '../config/adminScope';
import { useAdminScope } from '../context/AdminScopeContext';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';
const LOGIN_ORIGIN = (import.meta.env.VITE_LOGIN_ORIGIN as string) || 'http://login.localtest.me:3000';

export type AdminNavLink = {
  path: string;
  label: string;
  icon: SvgIconComponent;
  comingSoon?: boolean;
};

export function useAdminNavLinks() {
  const { t } = useTranslation();
  const { navigateWithLang, getPathWithoutLang } = useLanguage();
  const location = useLocation();
  const { scope, homePath, displayName, email } = useAdminScope();

  const currentPath = getPathWithoutLang(location.pathname);

  const carouselLinks: AdminNavLink[] = useMemo(() => {
    const sections = getNavSectionsForScope(scope);
    return sections.flatMap((section) =>
      section.items.map((item) => ({
        path: item.path,
        label: t(item.labelKey),
        icon: item.icon,
        comingSoon: item.comingSoon,
      })),
    );
  }, [scope, t]);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return currentPath === '/' || currentPath === '';
      return currentPath === path || currentPath.startsWith(`${path}/`);
    },
    [currentPath],
  );

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_BASE_URL}/api/v1/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      /* ignore */
    }
    window.location.replace(`${LOGIN_ORIGIN.replace(/\/$/, '')}/`);
  }, []);

  const handleNavClick = useCallback(
    (link: AdminNavLink) => {
      if (link.comingSoon) return;
      navigateWithLang(link.path);
    },
    [navigateWithLang],
  );

  return {
    carouselLinks,
    homePath,
    displayName,
    email,
    isActive,
    handleNavClick,
    handleLogout,
  };
}
